import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Menu,
	MenuItem,
} from '@mui/material'
import { useMemo, useState } from 'react'

import { useAppContext } from '@/hooks'

import { pickCompareTarget } from '../pickDefaultBranch'
import { BranchRecord } from '../types'

type CompareBranchDialogProps = {
	branch: BranchRecord
	branches: BranchRecord[]
	onClose: () => void
	onConfirm: (target: BranchRecord) => void
}

export const CompareBranchDialog = ({
	branch,
	branches,
	onClose,
	onConfirm,
}: CompareBranchDialogProps) => {
	const { t } = useAppContext()
	const options = useMemo(
		() =>
			branches.filter(
				(item) => item.status !== 'DELETED' && item.id !== branch.id,
			),
		[branch.id, branches],
	)
	const mainId = options.find((item) => !item.sourceBranchId)?.id
	const [targetId, setTargetId] = useState(
		() => pickCompareTarget(branch, branches) ?? options[0]?.id ?? '',
	)
	const [anchor, setAnchor] = useState<HTMLElement | null>(null)
	const target = options.find((item) => item.id === targetId)
	const labelFor = (item: BranchRecord) =>
		item.id === mainId ? t('BRANCHES_COMPARE_MAIN', [item.name]) : item.name

	return (
		<Dialog
			open
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
		>
			<DialogTitle
				sx={{ fontSize: 16, fontWeight: 700, color: '#1F4954', pb: '6px' }}
			>
				{t('BRANCHES_COMPARE_DIALOG')}
			</DialogTitle>
			<DialogContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '14px',
					pt: '8px !important',
				}}
			>
				<Box
					sx={{
						fontSize: '11px',
						fontWeight: 700,
						letterSpacing: '.04em',
						textTransform: 'uppercase',
						color: '#9A9A9A',
					}}
				>
					{t('BRANCHES_COMPARE_AGAINST', [branch.name])}
				</Box>
				<Box>
					<Box
						onClick={(event) => options.length && setAnchor(event.currentTarget)}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							cursor: options.length ? 'pointer' : 'default',
							border: '1px solid #DCDCDC',
							borderRadius: '9px',
							px: '11px',
							py: '9px',
							fontSize: '13px',
							color: '#2A2A2A',
							opacity: options.length ? 1 : 0.5,
							'&:hover': options.length ? { borderColor: '#1F4954' } : {},
						}}
						data-testid="branch-compare-target"
					>
						<AccountTreeOutlinedIcon sx={{ fontSize: 15, color: '#52666C' }} />
						<Box sx={{ flex: 1 }}>
							{target ? labelFor(target) : t('BRANCHES_COMPARE_SELECT')}
						</Box>
						<KeyboardArrowDownOutlinedIcon sx={{ fontSize: 14, color: '#B0B0B0' }} />
					</Box>
					<Menu
						anchorEl={anchor}
						open={Boolean(anchor)}
						onClose={() => setAnchor(null)}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
						transformOrigin={{ vertical: 'top', horizontal: 'left' }}
						slotProps={{
							paper: {
								sx: { minWidth: 260, mt: '4px', borderRadius: '10px', maxHeight: 360 },
							},
						}}
					>
						{options.map((item) => (
							<MenuItem
								key={item.id}
								selected={item.id === targetId}
								onClick={() => {
									setTargetId(item.id)
									setAnchor(null)
								}}
								sx={{ fontSize: 13, gap: '8px' }}
							>
								<Box sx={{ display: 'flex', color: '#52666C' }}>
									<AccountTreeOutlinedIcon sx={{ fontSize: 14 }} />
								</Box>
								{labelFor(item)}
							</MenuItem>
						))}
					</Menu>
				</Box>
				{!options.length ? (
					<Box sx={{ fontSize: 11, color: '#B0863A' }}>
						{t('BRANCHES_COMPARE_NO_TARGET')}
					</Box>
				) : null}
			</DialogContent>
			<DialogActions sx={{ px: '24px', pb: '18px' }}>
				<Button onClick={onClose} sx={{ color: '#6A6A6A', borderRadius: '8px' }}>
					{t('CANCEL')}
				</Button>
				<Button
					variant="contained"
					disableElevation
					disabled={!target}
					onClick={() => target && onConfirm(target)}
					sx={{ borderRadius: '8px', px: '18px' }}
					data-testid="branch-compare-confirm"
				>
					{t('BRANCHES_COMPARE')}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
